import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Shield, Lock, Server, Download, Eye, CheckCircle, Sparkles, AlertTriangle, Key, Database, UserCheck, Clock, Network, Globe, Mail, FileCheck, ShieldCheck } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import PDFDownloadLink from '../components/PDFDownloadLink';
import { TRUST_SECTIONS } from '../data/trustSections';

const DOCS = {
  pdf: 'https://customer-assets.emergentagent.com/job_c2bb8c09-4588-4153-b825-a008e90605ce/artifacts/lbhgmyyl_Forsyt_Security_Onboarding_Final_.pdf',
  nda: 'https://customer-assets.emergentagent.com/job_c2bb8c09-4588-4153-b825-a008e90605ce/artifacts/02jhxa1l_Forsyt-NDA.docx',
};

const PILLARS = [
  {
    icon: Lock,
    title: 'Zero-Data-Retention',
    desc: 'Prompts and responses are processed in-memory. Nothing is persisted on Forsyt servers after a session completes.',
    accent: 'bg-blue-500',
    bullets: ['In-memory only', 'No LLM fine-tuning on your data', 'Ephemeral processing'],
  },
  {
    icon: Shield,
    title: 'Legally Bound via NDA',
    desc: 'Every engagement begins with a mutual NDA. Your pipeline data stays confidential by contract, not just by policy.',
    accent: 'bg-emerald-500',
    bullets: ['Mutual NDA at kickoff', 'Named contract owner', 'Audit-ready provenance'],
  },
  {
    icon: Server,
    title: 'Deploy on Your Terms',
    desc: 'Hosted in our managed AWS cloud (EU / India / US regions) by default. Private VPC and on‑prem deployments available on request for Enterprise contracts.',
    accent: 'bg-purple-500',
    bullets: ['Managed AWS hosting', 'Private VPC on request', 'On-prem evaluated case-by-case'],
  },
  {
    icon: FileCheck,
    title: 'Partner-Led Setup',
    desc: 'A named technical contact from Forsyt owns every rollout. No self-serve surprises, no hidden data flows, no ambiguity.',
    accent: 'bg-amber-500',
    bullets: ['Named technical contact', 'Security review onboarded', 'White-glove rollout'],
  },
];

const CONTROLS = [
  { icon: Key, title: 'AES-256 encryption', desc: 'All data encrypted in transit (TLS 1.3) and at rest (AES-256).' },
  { icon: UserCheck, title: 'Role-based access control', desc: 'Granular RBAC for production access. Google Workspace SSO available today; SAML 2.0 / OIDC on the Enterprise roadmap.' },
  { icon: Network, title: 'Isolated tenancy', desc: 'Logical tenant isolation on shared infrastructure today. Single-tenant private VPC deployments available on request for Enterprise contracts.' },
  { icon: Database, title: 'Provenance-aware AI', desc: 'AI Deal Chat is grounded in your pipeline context. Source citation for every insight is an active product priority.' },
  { icon: Clock, title: 'In-memory processing', desc: 'Prompts, responses, and intermediate computations exist only for the session lifetime.' },
  { icon: Shield, title: 'Mutual NDA first', desc: 'No data, no pilot conversation, no access, until both sides have signed a mutual NDA.' },
];

const LIFECYCLE = [
  { phase: 'Intake', text: 'Data enters Forsyt through an authenticated API call from your CRM, call system, or email server.' },
  { phase: 'Process', text: 'Relevant fields are read in-memory, classified, and transformed into structured signals.' },
  { phase: 'Reason', text: 'LLM reasoning happens in an isolated session, prompts and context stay within your tenant boundary.' },
  { phase: 'Return', text: 'Outputs are surfaced in the Forsyt UI. CRM write-back is available where the integration is enabled by your admin, with explicit field-level scope.' },
  { phase: 'Purge', text: 'Session memory is flushed. Only the final field updates (already yours) remain, in your CRM, not ours.' },
];

const FAQ = [
  { q: 'Do you train AI models on our data?', a: 'No. Customer data is never used to train or fine-tune shared models. All reasoning happens against your tenant context in-memory, and is discarded after the session.' },
  { q: 'Where is my data processed?', a: 'By default, in our hosted AWS cloud region closest to you (AWS Mumbai for India, AWS Frankfurt for EU, AWS Virginia for North America on request). Private VPC and on-prem deployments are available on request for Enterprise contracts.' },
  { q: 'What certifications do you hold today?', a: 'Forsyt is ISO/IEC 27001:2022 certified, our Information Security Management System (ISMS) covers the design, development, hosting, and operation of the Forsyt Revenue OS platform and supporting business functions. We are GDPR compliant for EU/UK customers (see GDPR section above). Our certificate, scope statement, Statement of Applicability (SoA) summary, and security-onboarding pack are available on request under NDA.' },
  { q: 'Do you integrate with our SSO / identity provider?', a: 'Google Workspace SSO is available today. SAML 2.0 and OIDC support (Okta, Azure AD, custom IdPs) is on our Enterprise roadmap and can be prioritised as part of a pilot scoping conversation.' },
  { q: 'How do you handle data deletion?', a: 'Any field change Forsyt writes to your CRM can be reverted. Session context, transcripts, and intermediate prompts are never persisted to begin with.' },
  { q: 'Can we review your architecture before pilot?', a: 'Yes. We share a Security Overview document on request (privacy@forsyt.com) covering data flow, control posture, and threat model. We also welcome deep-dive sessions with your security team.' },
];

export default function SecurityPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO
        title="Security and Data Privacy, Forsyt Revenue OS"
        description="Zero-data-retention by design. ISO/IEC 27001:2022 certified, GDPR compliant, and covered by mutual NDA. Hosted in managed AWS regions; private VPC and on-prem available on request."
        path="/security"
      />

      {/* Hero */}
      <section className="relative pt-20 pb-16 bg-gray-50 overflow-hidden">
        {/* Grid pattern */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'linear-gradient(to right, #0f172a 1px, transparent 1px), linear-gradient(to bottom, #0f172a 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-50 border border-blue-100 mb-5">
            <Shield className="w-3.5 h-3.5 text-blue-600" />
            <span className="text-xs font-bold text-blue-700 uppercase tracking-widest">Enterprise-grade trust</span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-5 leading-tight tracking-tight" data-testid="security-headline">
            Security and Data Privacy,{' '}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              by design
            </span>
          </h1>
          <p className="text-base md:text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
            Revenue data is the most sensitive asset your company owns. Forsyt is engineered so it
            never leaves your control, by architecture, by contract, and by process.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <PDFDownloadLink
              variant="security"
              source="security-page-hero"
              className="inline-flex items-center gap-2 px-7 py-3 text-sm font-semibold bg-gradient-to-r from-emerald-600 to-blue-600 text-white rounded-full shadow-lg hover:shadow-xl transition-all"
            >
              <Download className="w-4 h-4" /> Download Security &amp; Trust Overview
            </PDFDownloadLink>
            <button
              onClick={() => {
                const a = document.createElement('a');
                a.href = DOCS.nda;
                a.download = 'Forsyt-NDA.docx';
                a.click();
              }}
              className="inline-flex items-center gap-2 px-7 py-3 text-sm font-semibold bg-white border border-gray-300 hover:border-blue-600 text-gray-900 rounded-full shadow-sm transition-all"
              data-testid="security-hero-nda"
            >
              <Download className="w-4 h-4" /> Download Mutual NDA
            </button>
          </div>
        </div>
      </section>

      {/* Compliance strip */}
      <section className="py-8 bg-white border-y border-gray-100">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-xs font-semibold uppercase tracking-widest text-gray-500">
            <span className="flex items-center gap-2" data-testid="security-strip-iso"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> ISO 27001 CERTIFIED</span>
            <span className="hidden sm:block w-px h-4 bg-gray-200" />
            <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> AES-256 IN TRANSIT &amp; AT REST</span>
            <span className="hidden sm:block w-px h-4 bg-gray-200" />
            <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> TLS 1.3 TRANSPORT</span>
            <span className="hidden sm:block w-px h-4 bg-gray-200" />
            <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> GDPR COMPLIANT</span>
            <span className="hidden sm:block w-px h-4 bg-gray-200" />
            <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> ROLE-BASED ACCESS</span>
            <span className="hidden sm:block w-px h-4 bg-gray-200" />
            <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> MUTUAL NDA REQUIRED</span>
          </div>
        </div>
      </section>

      {/* Certifications grid, ISO 27001 + GDPR */}
      <section className="py-12 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* ISO 27001 */}
            <div className="relative bg-gradient-to-br from-emerald-50 via-white to-emerald-50/30 border border-emerald-200 rounded-2xl p-6 flex items-start gap-5 overflow-hidden" data-testid="security-iso-card">
              <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-emerald-500/10" />
              <div className="relative shrink-0">
                <div className="w-20 h-20 rounded-2xl bg-white border-2 border-emerald-300 shadow-md flex flex-col items-center justify-center">
                  <ShieldCheck className="w-7 h-7 text-emerald-600" />
                  <span className="text-[8px] font-black text-emerald-700 tracking-widest mt-0.5">ISO 27001</span>
                </div>
              </div>
              <div className="relative">
                <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-widest mb-2">
                  <CheckCircle className="w-3 h-3" /> Certified
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1.5">ISO/IEC 27001:2022</h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-3">
                  Our Information Security Management System is independently audited and certified
                  against the international standard for information security. Scope covers the
                  Forsyt Revenue OS platform and all supporting business functions.
                </p>
                <p className="text-[11px] text-gray-500 font-mono bg-white/70 rounded-md px-2.5 py-1.5 border border-emerald-100 inline-block">
                  Standard: ISO/IEC 27001:2022 &middot; Last review: Feb 2026
                </p>
              </div>
            </div>

            {/* GDPR shortcut */}
            <a href="#gdpr" className="group relative bg-gradient-to-br from-blue-50 via-white to-blue-50/30 border border-blue-200 rounded-2xl p-6 flex items-start gap-5 overflow-hidden hover:shadow-md transition-all" data-testid="security-gdpr-card">
              <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-blue-500/10" />
              <div className="relative shrink-0">
                <div className="w-20 h-20 rounded-2xl bg-white border-2 border-blue-300 shadow-md flex flex-col items-center justify-center">
                  <Globe className="w-7 h-7 text-blue-600" />
                  <span className="text-[8px] font-black text-blue-700 tracking-widest mt-0.5">GDPR</span>
                </div>
              </div>
              <div className="relative">
                <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-blue-600 text-white text-[10px] font-bold uppercase tracking-widest mb-2">
                  <CheckCircle className="w-3 h-3" /> Compliant
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1.5 group-hover:text-blue-700 transition-colors">EU GDPR &amp; UK DPA 2018</h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-3">
                  Compliant data processor for revenue data of EU and UK residents. SCCs in place for
                  cross-border transfers; DPA available on request.
                </p>
                <span className="text-[11px] text-blue-600 font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                  Read GDPR section <ArrowRight className="w-3 h-3" />
                </span>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* 4 trust pillars */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-xs font-bold text-blue-700 uppercase tracking-widest mb-3">
              <Sparkles className="w-3 h-3" /> Four pillars
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
              How we protect your data
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  className="group relative bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-md transition-all overflow-hidden"
                  data-testid={`security-pillar-${i}`}
                >
                  <div className={`absolute top-6 bottom-6 left-0 w-1 rounded-r ${p.accent} opacity-70`} />
                  <div className="pl-3">
                    <Icon className="w-6 h-6 text-gray-400 mb-3 group-hover:text-gray-700 transition-colors" />
                    <h3 className="text-base font-bold text-gray-900 mb-2">{p.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4">{p.desc}</p>
                    <ul className="space-y-1.5">
                      {p.bullets.map((b, j) => (
                        <li key={j} className="flex items-center gap-2 text-xs text-gray-600">
                          <CheckCircle className="w-3 h-3 text-emerald-500 shrink-0" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Security controls grid */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-xs font-bold text-emerald-700 uppercase tracking-widest mb-3">
              <CheckCircle className="w-3 h-3" /> Controls inventory
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
              The controls we run by default
            </h2>
            <p className="text-sm md:text-base text-gray-600 mt-3 max-w-2xl mx-auto">
              A snapshot of technical controls applied to every customer, on day one.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CONTROLS.map((c, i) => {
              const Icon = c.icon;
              return (
                <div key={i} className="p-5 rounded-xl bg-gray-50 border border-gray-100 hover:border-blue-200 transition-colors" data-testid={`security-control-${i}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 rounded-lg bg-white border border-gray-200 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-blue-600" />
                    </div>
                    <h3 className="text-sm font-bold text-gray-900">{c.title}</h3>
                  </div>
                  <p className="text-xs md:text-sm text-gray-600 leading-relaxed pl-12">{c.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Data lifecycle flow */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 text-xs font-bold text-amber-700 uppercase tracking-widest mb-3">
              <Clock className="w-3 h-3" /> Data lifecycle
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
              What happens to a piece of data inside Forsyt
            </h2>
            <p className="text-sm md:text-base text-gray-600 mt-3 max-w-2xl mx-auto">
              Five stages, in-memory only, with nothing persisted along the way.
            </p>
          </div>
          <div className="relative pl-6 md:pl-8 space-y-6 border-l-2 border-dashed border-blue-200">
            {LIFECYCLE.map((step, i) => (
              <div key={i} className="relative" data-testid={`security-lifecycle-${i}`}>
                <div className="absolute -left-[35px] md:-left-[41px] top-1 w-6 h-6 rounded-full bg-blue-600 ring-4 ring-gray-50 flex items-center justify-center">
                  <span className="text-[10px] font-bold text-white">{i + 1}</span>
                </div>
                <div className="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-5">
                  <span className="text-xs font-bold text-blue-600 uppercase tracking-widest whitespace-nowrap md:min-w-[80px]">{step.phase}</span>
                  <p className="text-sm md:text-[15px] text-gray-700 leading-relaxed">{step.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-50 text-xs font-bold text-purple-700 uppercase tracking-widest mb-3">
              <AlertTriangle className="w-3 h-3" /> Security FAQ
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
              Questions security teams ask us first
            </h2>
          </div>
          <div className="space-y-3">
            {FAQ.map((item, i) => (
              <details key={i} className="group bg-gray-50 rounded-xl border border-gray-200 open:border-blue-200 open:shadow-sm transition-all" data-testid={`security-faq-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer p-5 text-sm md:text-base font-bold text-gray-900">
                  <span>{item.q}</span>
                  <span className="ml-4 text-blue-600 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                </summary>
                <p className="px-5 pb-5 text-sm text-gray-600 leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>

          {/* Link to embedded trust center sections */}
          <div className="mt-8 flex items-center justify-center">
            <a
              href="#trust-center"
              className="inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-full transition-all"
              data-testid="security-trust-link"
            >
              See full procurement Q&amp;A, 40+ buyer questions answered
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* GDPR section + badge */}
      <section className="py-16 bg-white border-t border-gray-100" id="gdpr">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
            {/* Left, narrative */}
            <div className="lg:col-span-3">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-xs font-bold text-blue-700 uppercase tracking-widest mb-3">
                <Globe className="w-3 h-3" /> EU &amp; UK customers
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight mb-4">
                GDPR Compliant by{' '}
                <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  architecture and contract
                </span>
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                Forsyt processes personal data in line with the EU General Data Protection
                Regulation (Regulation 2016/679) and the UK Data Protection Act 2018. We act as
                a <strong>data processor</strong> for our customers; you remain the data
                controller for the revenue data we process on your behalf.
              </p>

              {/* Data subject rights */}
              <div className="space-y-3 mb-6">
                <h3 className="text-sm font-bold text-gray-500 uppercase tracking-widest">Your rights as a data subject</h3>
                <ul className="grid sm:grid-cols-2 gap-2">
                  {[
                    'Right of access',
                    'Right to rectification',
                    'Right to erasure ("right to be forgotten")',
                    'Right to restrict processing',
                    'Right to data portability',
                    'Right to object to processing',
                    'Right not to be subject to automated decisions',
                    'Right to lodge a complaint with a supervisory authority',
                  ].map((r) => (
                    <li key={r} className="flex items-start gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Lawful basis */}
              <div className="space-y-3 mb-6">
                <h3 className="text-sm font-bold text-gray-500 uppercase tracking-widest">Lawful basis &amp; data residency</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-blue-600 font-bold mt-0.5">→</span>
                    <span><strong>Lawful basis:</strong> contract performance (Art. 6(1)(b)) for service provision; legitimate interest (Art. 6(1)(f)) for security and abuse prevention.</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-blue-600 font-bold mt-0.5">→</span>
                    <span><strong>Data residency:</strong> EU and UK customer data is processed in AWS Frankfurt (eu-central-1) by default. On-prem and customer-VPC deployments available.</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-blue-600 font-bold mt-0.5">→</span>
                    <span><strong>International transfers:</strong> covered by Standard Contractual Clauses (SCCs) where applicable, with supplementary technical measures.</span>
                  </li>
                  <li className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-blue-600 font-bold mt-0.5">→</span>
                    <span><strong>Breach notification:</strong> we notify affected customers within 72 hours of any confirmed personal-data breach, per Art. 33.</span>
                  </li>
                </ul>
              </div>

              {/* DPA + contact */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pt-2">
                <a
                  href="mailto:privacy@forsyt.com?subject=Data%20Processing%20Agreement%20Request"
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full shadow-md hover:shadow-lg transition-all"
                  data-testid="security-dpa-request"
                >
                  <FileCheck className="w-4 h-4" /> Request our DPA
                </a>
                <a
                  href="mailto:privacy@forsyt.com"
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold border border-gray-300 hover:border-blue-600 text-gray-900 rounded-full transition-all"
                  data-testid="security-dpo-contact"
                >
                  <Mail className="w-4 h-4 text-blue-600" /> Contact DPO
                </a>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Data subject requests are acknowledged within 5 business days and resolved within
                the GDPR&rsquo;s one-month statutory window. Complex requests may extend by up to two
                further months with notice.
              </p>
            </div>

            {/* Right, GDPR badge card */}
            <div className="lg:col-span-2">
              <div className="sticky top-28 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-2xl p-6 text-center" data-testid="security-gdpr-badge">
                {/* Stylized EU stars badge */}
                <div className="relative w-32 h-32 mx-auto mb-4">
                  {/* Outer ring */}
                  <div className="absolute inset-0 rounded-full bg-white shadow-lg flex items-center justify-center border-2 border-blue-200">
                    {/* Inner */}
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-600 to-indigo-700 flex flex-col items-center justify-center text-white">
                      <span className="text-[10px] font-bold tracking-widest opacity-80">GDPR</span>
                      <span className="text-2xl font-black leading-none mt-0.5">EU</span>
                      <span className="text-[9px] font-semibold tracking-wider opacity-90 mt-1">COMPLIANT</span>
                    </div>
                  </div>
                  {/* 12 stars around the ring (simplified, 6 visible) */}
                  {Array.from({ length: 12 }).map((_, i) => {
                    const angle = (i * 360) / 12 - 90;
                    const r = 60;
                    const x = 50 + (r * Math.cos((angle * Math.PI) / 180)) / 1.4;
                    const y = 50 + (r * Math.sin((angle * Math.PI) / 180)) / 1.4;
                    return (
                      <span
                        key={i}
                        className="absolute text-amber-400 text-[10px] -translate-x-1/2 -translate-y-1/2"
                        style={{ left: `${x}%`, top: `${y}%` }}
                      >
                        ★
                      </span>
                    );
                  })}
                </div>
                <p className="text-sm font-bold text-gray-900 mb-1">EU GDPR &amp; UK DPA 2018</p>
                <p className="text-xs text-gray-600 leading-relaxed mb-3">
                  Compliant data processor for revenue data of EU and UK residents.
                </p>
                <div className="text-[10px] text-gray-500 font-mono bg-white rounded-lg px-3 py-2 border border-blue-100">
                  <div className="flex items-center justify-between">
                    <span>Lawful basis</span>
                    <span className="font-bold text-gray-700">Art. 6(1)(b)/(f)</span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <span>Last review</span>
                    <span className="font-bold text-gray-700">Feb 2026</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════ TRUST CENTER, embedded buyer Q&A (40+ items) ═══════ */}
      <section id="trust-center" className="py-16 bg-gray-50 border-t border-gray-100 scroll-mt-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-xs font-bold text-blue-700 uppercase tracking-widest mb-3">
              <FileCheck className="w-3 h-3" /> Trust Center
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight mb-3">
              The answer to every security question your{' '}
              <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">procurement team</span>{' '}
              will ask
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              Instead of filling out a 200-question vendor questionnaire, point your buyer here.
              Every answer is committed to in writing and grounded in what Forsyt actually does.
            </p>
          </div>

          {/* Resource shortcuts */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-10">
            <a
              href="mailto:privacy@forsyt.com?subject=DPA%20template%20request"
              className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold bg-white border border-gray-200 hover:border-blue-300 rounded-full transition-all"
              data-testid="security-dpa-download"
            >
              <Mail className="w-3.5 h-3.5 text-blue-600" /> Request DPA template
            </a>
            <Link
              to="/sub-processors"
              className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold bg-white border border-gray-200 hover:border-blue-300 rounded-full transition-all"
              data-testid="security-subprocessors-link"
            >
              <Database className="w-3.5 h-3.5 text-blue-600" /> Sub-processors list
            </Link>
            <a
              href="mailto:privacy@forsyt.com?subject=Security%20questionnaire"
              className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold bg-white border border-gray-200 hover:border-blue-300 rounded-full transition-all"
              data-testid="security-trust-email"
            >
              <Mail className="w-3.5 h-3.5 text-blue-600" /> Email privacy@forsyt.com
            </a>
          </div>

          {/* TOC + content */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* TOC */}
            <aside className="lg:col-span-1">
              <div className="lg:sticky lg:top-28 space-y-1">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Categories</p>
                {TRUST_SECTIONS.map((s) => {
                  const Icon = s.icon;
                  return (
                    <a
                      key={s.id}
                      href={`#trust-${s.id}`}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                      data-testid={`security-toc-${s.id}`}
                    >
                      <Icon className="w-4 h-4 text-gray-400" />
                      <span>{s.title}</span>
                    </a>
                  );
                })}
              </div>
            </aside>

            {/* Content */}
            <div className="lg:col-span-3 space-y-12">
              {TRUST_SECTIONS.map((s) => {
                const Icon = s.icon;
                return (
                  <div key={s.id} id={`trust-${s.id}`} className="scroll-mt-24" data-testid={`security-trust-section-${s.id}`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-blue-600" />
                      </div>
                      <h3 className="text-xl md:text-2xl font-bold text-gray-900 tracking-tight">{s.title}</h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-5">{s.summary}</p>
                    <div className="space-y-2.5">
                      {s.qa.map((item, i) => (
                        <details
                          key={i}
                          className="group bg-white rounded-xl border border-gray-200 open:border-blue-200 open:shadow-sm transition-all"
                          data-testid={`security-trust-qa-${s.id}-${i}`}
                        >
                          <summary className="flex items-start justify-between cursor-pointer p-5 text-sm md:text-base font-bold text-gray-900 gap-4">
                            <span className="flex items-start gap-3 flex-1">
                              <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                              <span>{item.q}</span>
                            </span>
                            <span className="text-blue-600 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                          </summary>
                          <div className="px-5 pb-5 pl-[52px]">
                            <p className="text-sm text-gray-600 leading-relaxed">{item.a}</p>
                          </div>
                        </details>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-3 tracking-tight">
            Review our controls before your pilot
          </h2>
          <p className="text-base md:text-lg text-blue-100 mb-7 max-w-2xl mx-auto">
            Download the Security &amp; Trust Overview and the mutual NDA. We&apos;ll walk your security team through
            architecture and threat model on a 30-minute review call.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <PDFDownloadLink
              variant="security"
              source="security-page-cta"
              className="inline-flex items-center gap-2 px-7 py-3 text-sm font-semibold bg-white text-blue-700 hover:bg-blue-50 rounded-full shadow-lg transition-all"
            >
              <Download className="w-4 h-4" /> Download Security &amp; Trust Overview
            </PDFDownloadLink>
            <a
              href="https://calendar.app.google/nswB9pt6ALLZ8xoy7"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-7 py-3 text-sm font-semibold border border-white/40 hover:border-white hover:bg-white/10 rounded-full transition-all"
              data-testid="security-cta-call"
            >
              Book a Security Review
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          <p className="text-xs text-blue-200/80 mt-6">
            Or <button onClick={() => navigate('/')} className="underline hover:text-white" data-testid="security-home-link">back to Forsyt home</button> · All engagements begin with a mutual NDA
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
